/**
 * Admin Marketplace Management
 * Full CRUD for products, pricing management, and order handling
 */
import { useState, useEffect, useCallback, useRef } from "react";
import {
  Package,
  Search,
  Plus,
  Edit,
  Trash2,
  DollarSign,
  Tag,
  Image as ImageIcon,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Filter,
  Upload,
  Download,
  Eye,
  ShoppingCart,
  Truck,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Percent,
  Star,
  MoreVertical,
  FileSpreadsheet,
  Boxes,
  FileText,
  Mail,
  Link as LinkIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/App";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Category configuration
const CATEGORIES = [
  { id: "all", name: "All Categories" },
  { id: "Infection Prevention", name: "Infection Prevention" },
  { id: "Janitorial & EVS", name: "Janitorial & EVS" },
  { id: "Water Purification", name: "Water Purification" },
];

// Filmop sub-categories (matching original Excel spreadsheet tabs)
const FILMOP_SUBCATEGORIES = [
  { id: "all", name: "All Janitorial" },
  { id: "Tools, Textiles, Buckets & Bins", name: "Tools, Textiles, Buckets & Bins" },
  { id: "Alpha Workstations", name: "Alpha Workstations" },
  { id: "AB Plus Workstations", name: "AB Plus Workstations" },
  { id: "Omega Carts", name: "Omega Carts" },
];

// Order status configuration
const ORDER_STATUSES = [
  { id: "pending", label: "Pending", color: "bg-yellow-100 text-yellow-800" },
  { id: "confirmed", label: "Confirmed", color: "bg-blue-100 text-blue-800" },
  { id: "processing", label: "Processing", color: "bg-indigo-100 text-indigo-800" },
  { id: "shipped", label: "Shipped", color: "bg-purple-100 text-purple-800" },
  { id: "delivered", label: "Delivered", color: "bg-green-100 text-green-800" },
  { id: "cancelled", label: "Cancelled", color: "bg-red-100 text-red-800" },
];

// Product Form Modal
const ProductFormModal = ({ product, onClose, onSave }) => {
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    item_number: product?.item_number || "",
    description: product?.description || "",
    uom: product?.uom || "Each",
    case_pack: product?.case_pack || 1,
    net_price: product?.net_price || 0,
    customer_price: product?.customer_price || 0,
    category: product?.category || "Tools Textiles Buckets Bins",
    manufacturer: product?.manufacturer || "Filmop USA",
    image_url: product?.image_url || "",
    lead_time_days: product?.lead_time_days || 3,
    discount_percent: product?.discount_percent || 0,
    in_stock: product?.in_stock ?? true,
    featured: product?.featured || false,
    active: product?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [categoriesList, setCategoriesList] = useState([]);
  const [imageMode, setImageMode] = useState("upload"); // 'upload' | 'url'
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Fetch the same dynamic category list used by AdminOrdersBoard's AddProductDialog
  useEffect(() => {
    fetch(`${API}/admin/orders/marketplace-categories`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const items = d?.categories || [];
        // Merge in the legacy Filmop categories so existing marketplace rows still edit cleanly
        const legacy = CATEGORIES.filter((c) => c.id !== "all").map((c) => ({ id: c.id, name: c.name }));
        const seen = new Set(items.map((c) => c.id));
        legacy.forEach((c) => { if (!seen.has(c.id)) items.push(c); });
        setCategoriesList(items);
      })
      .catch(() => {
        setCategoriesList(CATEGORIES.filter((c) => c.id !== "all"));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Absolutize any relative URL the upload endpoint returns
  const absolutize = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    const base = process.env.REACT_APP_BACKEND_URL;
    if (url.startsWith("/api/static/")) return `${base}${url}`;
    if (url.startsWith("/static/")) return `${base}/api${url}`;
    return url;
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (!token) {
      toast.error("Not signed in — cannot upload image");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/admin/orders/upload-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${r.status})`);
      }
      const data = await r.json();
      setFormData((f) => ({ ...f, image_url: absolutize(data.url) }));
      toast.success(`Uploaded ${(data.size_bytes / 1024).toFixed(1)} KB`);
    } catch (e) {
      toast.error(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      toast.error(error.message || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  // Calculate effective price with discount
  const effectivePrice = formData.customer_price * (1 - formData.discount_percent / 100);
  const margin = ((formData.customer_price - formData.net_price) / formData.customer_price * 100) || 0;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-emerald-600" />
            {product ? "Edit Product" : "Add New Product"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Item Number *</label>
              <Input
                value={formData.item_number}
                onChange={(e) => setFormData({ ...formData, item_number: e.target.value })}
                placeholder="e.g., 9152"
                required
                disabled={!!product}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Manufacturer</label>
              <Input
                value={formData.manufacturer}
                onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                placeholder="Filmop USA"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700">Description *</label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Product description..."
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Category</label>
              <Select
                value={formData.category}
                onValueChange={(v) => setFormData({ ...formData, category: v })}
              >
                <SelectTrigger data-testid="mkt-form-category-trigger">
                  <SelectValue placeholder="Pick a category…" />
                </SelectTrigger>
                <SelectContent className="max-h-72 overflow-y-auto">
                  {categoriesList.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id} data-testid={`mkt-form-category-${cat.id}`}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">UOM</label>
              <Select
                value={formData.uom}
                onValueChange={(v) => setFormData({ ...formData, uom: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Each">Each</SelectItem>
                  <SelectItem value="Case">Case</SelectItem>
                  <SelectItem value="Pack">Pack</SelectItem>
                  <SelectItem value="Set">Set</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Case Pack</label>
              <Input
                type="number"
                value={formData.case_pack}
                onChange={(e) => setFormData({ ...formData, case_pack: parseInt(e.target.value) || 1 })}
                min="1"
              />
            </div>
          </div>

          {/* Pricing Section */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Pricing
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Net/Wholesale Price</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.net_price}
                    onChange={(e) => setFormData({ ...formData, net_price: parseFloat(e.target.value) || 0 })}
                    className="pl-7"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Your cost</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Customer Price</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.customer_price}
                    onChange={(e) => setFormData({ ...formData, customer_price: parseFloat(e.target.value) || 0 })}
                    className="pl-7"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Hospital sees this</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Discount %</label>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.discount_percent}
                    onChange={(e) => setFormData({ ...formData, discount_percent: parseFloat(e.target.value) || 0 })}
                    className="pr-7"
                    max="100"
                    min="0"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                </div>
              </div>
            </div>
            {/* Pricing Summary */}
            <div className="flex gap-4 text-sm pt-2 border-t border-slate-200">
              <span className="text-slate-600">
                Effective Price: <strong className="text-emerald-600">${effectivePrice.toFixed(2)}</strong>
              </span>
              <span className="text-slate-600">
                Margin: <strong className={margin > 20 ? "text-green-600" : "text-amber-600"}>{margin.toFixed(1)}%</strong>
              </span>
            </div>
          </div>

          {/* Additional Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Lead Time (Days)</label>
              <Input
                type="number"
                value={formData.lead_time_days}
                onChange={(e) => setFormData({ ...formData, lead_time_days: parseInt(e.target.value) || 3 })}
                min="1"
              />
              <p className="text-xs text-slate-500 mt-1">TX: 2 days, Others: 3-5 days</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Product Image</label>
              <div className="flex gap-1 mb-2">
                <button
                  type="button"
                  onClick={() => setImageMode("upload")}
                  className={`text-[11px] px-2.5 py-1 rounded border font-semibold ${imageMode === "upload" ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "border-slate-200 text-slate-500"}`}
                  data-testid="mkt-form-img-tab-upload"
                >
                  <Upload className="w-3 h-3 inline mr-1" /> Upload File
                </button>
                <button
                  type="button"
                  onClick={() => setImageMode("url")}
                  className={`text-[11px] px-2.5 py-1 rounded border font-semibold ${imageMode === "url" ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "border-slate-200 text-slate-500"}`}
                  data-testid="mkt-form-img-tab-url"
                >
                  <LinkIcon className="w-3 h-3 inline mr-1" /> Paste URL
                </button>
              </div>
              {imageMode === "upload" ? (
                <div
                  className="border-2 border-dashed border-slate-300 rounded p-4 text-center text-xs text-slate-500 hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors cursor-pointer"
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); handleUpload(e.dataTransfer.files?.[0]); }}
                  data-testid="mkt-form-upload-dropzone"
                >
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp,.gif"
                    className="hidden"
                    onChange={(e) => handleUpload(e.target.files?.[0])}
                    data-testid="mkt-form-upload-input"
                  />
                  {uploading ? "Uploading…" : formData.image_url ? "Click or drag to replace image" : "Click or drag a JPG/PNG/WEBP (≤8 MB)"}
                </div>
              ) : (
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://supplier.com/path/to/image.jpg"
                  data-testid="mkt-form-image-url"
                />
              )}
              {formData.image_url && (
                <div className="mt-2 flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded">
                  <img src={formData.image_url} alt="" className="w-10 h-10 rounded object-cover bg-white" data-testid="mkt-form-preview-img" />
                  <div className="text-[11px] text-slate-600 flex-1 truncate font-mono">{formData.image_url}</div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, image_url: "" })}
                    className="text-slate-400 hover:text-red-600"
                    data-testid="mkt-form-clear-img"
                    aria-label="Clear image"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Toggles */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.in_stock}
                onChange={(e) => setFormData({ ...formData, in_stock: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-sm text-slate-700">In Stock</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.featured}
                onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
              />
              <span className="text-sm text-slate-700">Featured</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-slate-700">Active (Visible)</span>
            </label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Product
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Order Detail Modal
const OrderDetailModal = ({ order, onClose, onUpdateStatus }) => {
  const [newStatus, setNewStatus] = useState(order.status);
  const [trackingNumber, setTrackingNumber] = useState(order.tracking_number || "");
  const [notes, setNotes] = useState("");
  const [updating, setUpdating] = useState(false);

  const handleUpdateStatus = async () => {
    if (newStatus === order.status && !notes) return;
    setUpdating(true);
    try {
      await onUpdateStatus(order.id, newStatus, trackingNumber, notes);
      onClose();
    } catch (error) {
      toast.error(error.message || "Failed to update order");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-emerald-600" />
            Order {order.order_number}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Order Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-medium text-slate-800 mb-2">Customer</h4>
              <p className="text-slate-600">{order.hospital_name}</p>
              <p className="text-sm text-slate-500">{order.hospital_email}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-medium text-slate-800 mb-2">Order Details</h4>
              <p className="text-sm text-slate-600">
                <span className="font-medium">Payment:</span> {order.payment_method === "purchase_order" ? "Purchase Order" : "Credit Card"}
              </p>
              {order.payment_terms && (
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Terms:</span> {order.payment_terms === "net30" ? "Net 30" : "Pay Upon Receipt"}
                </p>
              )}
              {order.po_number && (
                <p className="text-sm text-slate-600">
                  <span className="font-medium">PO #:</span> {order.po_number}
                </p>
              )}
              {order.has_po_file && (
                <a
                  href={`${API}/marketplace/orders/${order.id}/po-download`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                  data-testid="download-po-file"
                >
                  <Download size={14} />
                  Download PO ({order.po_file_name || "purchase_order.pdf"})
                </a>
              )}
              <p className="text-sm text-slate-500 mt-1">
                {new Date(order.created_at).toLocaleString()}
              </p>
            </div>
          </div>

          {/* Line Items */}
          <div>
            <h4 className="font-medium text-slate-800 mb-3">Items</h4>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3">Item</th>
                    <th className="text-center p-3">Qty</th>
                    <th className="text-right p-3">Unit Price</th>
                    <th className="text-right p-3">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="p-3">
                        <p className="font-medium">{item.item_number}</p>
                        <p className="text-xs text-slate-500">{item.description}</p>
                      </td>
                      <td className="p-3 text-center">{item.quantity}</td>
                      <td className="p-3 text-right">${item.unit_price?.toFixed(2)}</td>
                      <td className="p-3 text-right font-medium">${item.line_total?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 border-t">
                  <tr>
                    <td colSpan="3" className="p-3 text-right font-medium">Subtotal:</td>
                    <td className="p-3 text-right">${order.subtotal?.toFixed(2)}</td>
                  </tr>
                  {order.discount_total > 0 && (
                    <tr>
                      <td colSpan="3" className="p-3 text-right font-medium text-green-600">Discount:</td>
                      <td className="p-3 text-right text-green-600">-${order.discount_total?.toFixed(2)}</td>
                    </tr>
                  )}
                  <tr className="text-lg">
                    <td colSpan="3" className="p-3 text-right font-bold">Total:</td>
                    <td className="p-3 text-right font-bold text-emerald-600">${order.total?.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Status Update */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-slate-800">Update Status</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-600">Status</label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ORDER_STATUSES.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-slate-600">Tracking Number</label>
                <Input
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder="Enter tracking number..."
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-600">Notes</label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add a note about this status change..."
              />
            </div>
          </div>

          {/* Existing Notes */}
          {order.notes && (
            <div>
              <h4 className="font-medium text-slate-800 mb-2">Order Notes</h4>
              <pre className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap">{order.notes}</pre>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            onClick={handleUpdateStatus}
            disabled={updating || (newStatus === order.status && !notes)}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {updating ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
            Update Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminMarketplace() {
  const [activeTab, setActiveTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [subCategoryFilter, setSubCategoryFilter] = useState("all");
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [initializing, setInitializing] = useState(false);
  const [uploadingPrices, setUploadingPrices] = useState(false);
  const pricesCsvInputRef = useRef(null);
  const [uploadingAsp, setUploadingAsp] = useState(false);
  const aspCsvInputRef = useRef(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalProducts, setTotalProducts] = useState(0);

  // Fetch products with pagination
  const fetchProducts = useCallback(async () => {
    try {
      const skip = (currentPage - 1) * pageSize;
      const params = new URLSearchParams({
        include_admin_pricing: "true",
        active_only: "false",
        skip: skip.toString(),
        limit: pageSize.toString(),
      });
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      if (categoryFilter === "Janitorial & EVS" && subCategoryFilter !== "all") params.append("subcategory", subCategoryFilter);
      if (searchQuery) params.append("search", searchQuery);

      const res = await fetch(`${API}/marketplace/products?${params}`);
      const data = await res.json();
      setProducts(data.products || []);
      setTotalProducts(data.total || 0);
    } catch (error) {
      console.error("Failed to fetch products:", error);
      toast.error("Failed to load products");
    }
  }, [categoryFilter, subCategoryFilter, searchQuery, currentPage, pageSize]);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (orderStatusFilter !== "all") params.append("status", orderStatusFilter);

      const res = await fetch(`${API}/marketplace/orders?${params}`);
      const data = await res.json();
      setOrders(data.orders || []);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    }
  }, [orderStatusFilter]);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/marketplace/admin/stats`);
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  // Load all data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchProducts(), fetchOrders(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchProducts, fetchOrders]);

  // Initialize Filmop products
  const handleInitializeFilmop = async () => {
    setInitializing(true);
    try {
      const res = await fetch(`${API}/marketplace/admin/initialize-filmop`, { method: "POST" });
      const data = await res.json();
      toast.success(`Loaded ${data.imported} new products, updated ${data.updated}`);
      fetchProducts();
      fetchStats();
    } catch (error) {
      toast.error("Failed to initialize products");
    } finally {
      setInitializing(false);
    }
  };

  // Bulk-price CSV export (opens a download in a new tab)
  const handleDownloadPricesCsv = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/marketplace/products/prices.csv`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sterilemate-marketplace-prices-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${res.headers.get("X-Row-Count") || ""} product prices`);
    } catch (err) {
      toast.error(err.message || "Could not download prices CSV");
    }
  };

  // Bulk-price CSV upload — user picks the edited CSV and we call the
  // upsert endpoint, then refresh the products grid.
  const handleUploadPricesCsv = async (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      toast.error("Please choose a .csv file.");
      return;
    }
    setUploadingPrices(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/marketplace/products/prices.csv`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Upload failed (${res.status})`);
      toast.success(
        `Prices updated · ${data.updated} changed · ${data.created} created`,
        {
          description: data.error_count > 0
            ? `${data.error_count} row error${data.error_count === 1 ? "" : "s"} — see console for detail.`
            : undefined,
        },
      );
      if (data.errors?.length) {
        console.warn("Bulk-price upload errors:", data.errors);
      }
      fetchProducts();
    } catch (err) {
      toast.error(err.message || "Could not upload prices CSV");
    } finally {
      setUploadingPrices(false);
      if (pricesCsvInputRef.current) pricesCsvInputRef.current.value = "";
    }
  };

  // American Surgical CSV download — separate collection from the marketplace
  const handleDownloadAspCsv = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/american-surgical/prices.csv`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `american-surgical-prices-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${res.headers.get("X-Row-Count") || ""} American Surgical SKUs`);
    } catch (err) {
      toast.error(err.message || "Could not download American Surgical CSV");
    }
  };

  const handleUploadAspCsv = async (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      toast.error("Please choose a .csv file.");
      return;
    }
    setUploadingAsp(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/american-surgical/prices.csv`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Upload failed (${res.status})`);
      toast.success(
        `American Surgical prices updated · ${data.updated} changed · ${data.created} created`,
        {
          description: data.error_count > 0
            ? `${data.error_count} row error${data.error_count === 1 ? "" : "s"} — see console for detail.`
            : undefined,
        },
      );
      if (data.errors?.length) {
        console.warn("ASP CSV upload errors:", data.errors);
      }
    } catch (err) {
      toast.error(err.message || "Could not upload American Surgical CSV");
    } finally {
      setUploadingAsp(false);
      if (aspCsvInputRef.current) aspCsvInputRef.current.value = "";
    }
  };

  // Save product
  const handleSaveProduct = async (productData) => {
    const isEdit = !!editingProduct;
    const url = isEdit
      ? `${API}/marketplace/admin/products/${editingProduct.id}`
      : `${API}/marketplace/admin/products`;
    const method = isEdit ? "PUT" : "POST";

    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(productData),
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to save product");
    }

    toast.success(isEdit ? "Product updated" : "Product created");
    fetchProducts();
    fetchStats();
  };

  // Delete product
  const handleDeleteProduct = async (productId, hardDelete = false) => {
    if (!window.confirm(hardDelete ? "Permanently delete this product?" : "Deactivate this product?")) {
      return;
    }

    try {
      const res = await fetch(`${API}/marketplace/admin/products/${productId}?hard_delete=${hardDelete}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete");
      toast.success(hardDelete ? "Product deleted" : "Product deactivated");
      fetchProducts();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  // Update order status
  const handleUpdateOrderStatus = async (orderId, status, trackingNumber, notes) => {
    const res = await fetch(`${API}/marketplace/admin/orders/${orderId}/status`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status, tracking_number: trackingNumber, notes }),
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to update order");
    }

    toast.success("Order updated");
    fetchOrders();
  };

  const getStatusBadge = (status) => {
    const statusConfig = ORDER_STATUSES.find(s => s.id === status);
    return statusConfig ? (
      <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
    ) : (
      <Badge variant="outline">{status}</Badge>
    );
  };

  // Pagination helpers
  const totalPages = Math.ceil(totalProducts / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalProducts);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, subCategoryFilter, searchQuery, pageSize]);

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(parseInt(newSize));
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <Boxes className="w-7 h-7 text-emerald-600" />
            Marketplace Management
          </h1>
          <p className="text-slate-500 mt-1">Manage products, pricing, and orders</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleInitializeFilmop}
            disabled={initializing}
          >
            {initializing ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            Load Filmop Products
          </Button>
          <Button
            onClick={handleDownloadPricesCsv}
            variant="outline"
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="marketplace-download-prices-csv"
            title="Download every product's pricing to a CSV file you can edit in Excel"
          >
            <Download className="w-4 h-4 mr-2" />
            Prices CSV ↓
          </Button>
          <Button
            onClick={() => pricesCsvInputRef.current?.click()}
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="marketplace-upload-prices-csv"
            title="Upload an edited CSV to bulk-update every product's price at once"
            disabled={uploadingPrices}
          >
            {uploadingPrices ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            Prices CSV ↑
          </Button>
          <input
            type="file"
            ref={pricesCsvInputRef}
            className="hidden"
            accept=".csv,text/csv"
            onChange={(e) => handleUploadPricesCsv(e.target.files?.[0])}
            data-testid="marketplace-prices-csv-file-input"
          />

          {/* American Surgical CSV — separate collection, same UX */}
          <Button
            onClick={handleDownloadAspCsv}
            variant="outline"
            className="border-violet-300 text-violet-700 hover:bg-violet-50"
            data-testid="asp-download-prices-csv"
            title="Download the American Surgical retail + cost pricing to CSV"
          >
            <Download className="w-4 h-4 mr-2" />
            Am. Surgical CSV ↓
          </Button>
          <Button
            onClick={() => aspCsvInputRef.current?.click()}
            variant="outline"
            className="border-violet-300 text-violet-700 hover:bg-violet-50"
            data-testid="asp-upload-prices-csv"
            title="Upload an edited American Surgical CSV to bulk-update retail prices"
            disabled={uploadingAsp}
          >
            {uploadingAsp ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            Am. Surgical CSV ↑
          </Button>
          <input
            type="file"
            ref={aspCsvInputRef}
            className="hidden"
            accept=".csv,text/csv"
            onChange={(e) => handleUploadAspCsv(e.target.files?.[0])}
            data-testid="asp-prices-csv-file-input"
          />
          <Button
            onClick={() => {
              setEditingProduct(null);
              setShowProductForm(true);
            }}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.total_products}</p>
                <p className="text-sm text-slate-500">Products</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.total_orders}</p>
                <p className="text-sm text-slate-500">Total Orders</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.pending_orders}</p>
                <p className="text-sm text-slate-500">Pending</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">${stats.total_revenue?.toLocaleString()}</p>
                <p className="text-sm text-slate-500">Revenue</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="products" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Products ({totalProducts})
          </TabsTrigger>
          <TabsTrigger value="orders" className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" />
            Orders ({orders.length})
          </TabsTrigger>
        </TabsList>

        {/* Products Tab */}
        <TabsContent value="products" className="mt-4">
          {/* Filters */}
          <div className="flex gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={categoryFilter} onValueChange={(val) => { setCategoryFilter(val); setSubCategoryFilter("all"); }}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Page Size Selector */}
            <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
              <SelectTrigger className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25 per page</SelectItem>
                <SelectItem value="50">50 per page</SelectItem>
                <SelectItem value="100">100 per page</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sub-category Filter for Janitorial & EVS */}
          {categoryFilter === "Janitorial & EVS" && (
            <div className="flex gap-2 overflow-x-auto mb-4" data-testid="admin-subcategory-filter">
              {FILMOP_SUBCATEGORIES.map(sub => (
                <button
                  key={sub.id}
                  onClick={() => setSubCategoryFilter(sub.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors border ${
                    subCategoryFilter === sub.id
                      ? "bg-[#003366] text-white border-[#003366]"
                      : "bg-white text-slate-600 border-slate-300 hover:border-[#003366] hover:text-[#003366]"
                  }`}
                  data-testid={`admin-subfilter-${sub.id}`}
                >
                  {sub.name}
                </button>
              ))}
            </div>
          )}

          {/* Products Table */}
          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Product</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Category</th>
                  <th className="text-right p-4 text-sm font-medium text-slate-600">Net Price</th>
                  <th className="text-right p-4 text-sm font-medium text-slate-600">Customer Price</th>
                  <th className="text-right p-4 text-sm font-medium text-slate-600">Discount</th>
                  <th className="text-center p-4 text-sm font-medium text-slate-600">Status</th>
                  <th className="text-right p-4 text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="p-8 text-center">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                    </td>
                  </tr>
                ) : products.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="p-8 text-center text-slate-500">
                      No products found. Click &ldquo;Load Filmop Products&rdquo; to import catalog.
                    </td>
                  </tr>
                ) : (
                  products.map(product => (
                    <tr key={product.id} className={`border-b hover:bg-slate-50 ${!product.active ? 'opacity-50' : ''}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {product.image_url ? (
                            <img src={product.image_url} alt="" className="w-10 h-10 rounded bg-slate-50 object-contain p-0.5" />
                          ) : (
                            <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
                              <Package className="w-5 h-5 text-slate-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-slate-800">{product.item_number}</p>
                            <p className="text-sm text-slate-500 line-clamp-1 max-w-xs">{product.description}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge variant="outline">{product.category}</Badge>
                      </td>
                      <td className="p-4 text-right font-mono text-slate-600">${product.net_price?.toFixed(2)}</td>
                      <td className="p-4 text-right font-mono text-slate-800 font-medium">${product.customer_price?.toFixed(2)}</td>
                      <td className="p-4 text-right">
                        {product.discount_percent > 0 ? (
                          <Badge className="bg-green-100 text-green-800">{product.discount_percent}% OFF</Badge>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {product.featured && <Star className="w-4 h-4 text-amber-500 fill-amber-500" />}
                          {product.in_stock ? (
                            <Badge className="bg-emerald-100 text-emerald-800">In Stock</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">Out of Stock</Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => {
                              setEditingProduct(product);
                              setShowProductForm(true);
                            }}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDeleteProduct(product.id, false)}>
                              <Eye className="w-4 h-4 mr-2" />
                              {product.active ? "Deactivate" : "Activate"}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => handleDeleteProduct(product.id, true)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Permanently
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            
            {/* Pagination Controls */}
            {totalProducts > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
                <div className="text-sm text-slate-600">
                  Showing <span className="font-medium">{startItem}</span> to <span className="font-medium">{endItem}</span> of{" "}
                  <span className="font-medium">{totalProducts}</span> products
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="px-3 py-1 text-sm font-medium bg-white border rounded">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="mt-4">
          {/* Filters */}
          <div className="flex gap-4 mb-4">
            <Select value={orderStatusFilter} onValueChange={setOrderStatusFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {ORDER_STATUSES.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Orders Table */}
          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Order #</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Customer</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Payment</th>
                  <th className="text-right p-4 text-sm font-medium text-slate-600">Total</th>
                  <th className="text-center p-4 text-sm font-medium text-slate-600">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Date</th>
                  <th className="text-right p-4 text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="p-8 text-center text-slate-500">
                      No orders yet
                    </td>
                  </tr>
                ) : (
                  orders.map(order => (
                    <tr key={order.id} className="border-b hover:bg-slate-50">
                      <td className="p-4 font-medium text-slate-800">{order.order_number}</td>
                      <td className="p-4">
                        <p className="text-slate-800">{order.hospital_name}</p>
                        <p className="text-sm text-slate-500">{order.hospital_email}</p>
                      </td>
                      <td className="p-4">
                        {order.payment_method === "purchase_order" ? (
                          <div>
                            <Badge variant="outline">PO</Badge>
                            {order.po_number && (
                              <p className="text-xs text-slate-500 mt-1">#{order.po_number}</p>
                            )}
                          </div>
                        ) : (
                          <Badge variant="outline">Credit Card</Badge>
                        )}
                      </td>
                      <td className="p-4 text-right font-bold text-emerald-600">${order.total?.toFixed(2)}</td>
                      <td className="p-4 text-center">{getStatusBadge(order.status)}</td>
                      <td className="p-4 text-sm text-slate-500">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {order.has_po_file && (
                            <a
                              href={`${API}/marketplace/orders/${order.id}/po-download`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-50 text-emerald-700 rounded-md hover:bg-emerald-100 transition-colors border border-emerald-200"
                              data-testid={`download-po-${order.id}`}
                            >
                              <Download className="w-3 h-3" />
                              PO
                            </a>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedOrder(order)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Product Form Modal */}
      {showProductForm && (
        <ProductFormModal
          product={editingProduct}
          onClose={() => {
            setShowProductForm(false);
            setEditingProduct(null);
          }}
          onSave={handleSaveProduct}
        />
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onUpdateStatus={handleUpdateOrderStatus}
        />
      )}
    </div>
  );
}
